import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { BRAND_NAME } from '../constants';

type PolicyType = 'legal' | 'privacy' | 'cookies';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: PolicyType;
}

const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, type }) => {
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getContent = () => {
    switch (type) {
      case 'legal':
        return {
          title: "Aviso Legal",
          content: (
            <div className="space-y-6 text-sm font-light text-slate-400 leading-relaxed">
              <div className="p-4 border border-slate-800 bg-slate-900/50">
                <p className="font-mono text-xs uppercase tracking-wider text-sage-400 mb-2">Datos Identificativos</p>
                <ul className="space-y-2">
                  <li><strong className="text-slate-300">Identidad:</strong> {BRAND_NAME} (Gestión de Capitales)</li>
                  <li><strong className="text-slate-300">NIF:</strong></li>
                  <li><strong className="text-slate-300">Ubicación:</strong>España.</li>
                  <li><strong className="text-slate-300">Contacto:</strong> contacto@carrionbet.com</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-medium mb-2">Propiedad Intelectual</h4>
                <p>
                  Todo el contenido mostrado en este sitio web (textos, gráficos, logotipos, análisis y estructura) es propiedad exclusiva del titular de {BRAND_NAME}. 
                  Queda prohibida su reproducción, distribución o modificación sin autorización expresa.
                </p>
              </div>

              <div>
                <h4 className="text-white font-medium mb-2">Responsabilidad de Enlaces</h4>
                <p>
                  Este sitio web puede contener enlaces a sitios externos (como Twitter/X, Telegram o plataformas de terceros). 
                  El titular no ejerce ningún control sobre dichos sitios y declina toda responsabilidad sobre el contenido, políticas o prácticas de los mismos. 
                  El usuario accede a estos contenidos bajo su propia responsabilidad.
                </p>
              </div>
            </div>
          )
        };
      case 'privacy':
        return {
          title: "Política de Privacidad",
          content: (
            <div className="space-y-6 text-sm font-light text-slate-400 leading-relaxed">
              <p className="border-l-2 border-sage-400 pl-4 italic text-slate-500">
                Transparencia absoluta: No necesitamos tus datos para mostrarte nuestro trabajo.
              </p>

              <div>
                <h4 className="text-white font-medium mb-2">Tratamiento de Datos</h4>
                <p>
                  En {BRAND_NAME} no recogemos, almacenamos ni procesamos datos personales de los usuarios. 
                  Este sitio web no dispone de formularios de contacto, registros de usuarios ni sistemas de comentarios. 
                  Tu navegación es totalmente anónima respecto a nosotros.
                </p>
              </div>

              <div>
                <h4 className="text-white font-medium mb-2">Servicios de Terceros (Seguridad)</h4>
                <p>
                  Para garantizar la seguridad y velocidad de la web, utilizamos los servicios de <strong>Cloudflare</strong>. 
                  Este proveedor puede procesar datos técnicos mínimos (como la dirección IP) exclusivamente para funciones de seguridad 
                  (bloqueo de ataques DDoS, bots maliciosos) y optimización de entrega de contenido (CDN).
                </p>
              </div>

              <div>
                <h4 className="text-white font-medium mb-2">Tus Derechos</h4>
                <p>
                  Aunque no mantenemos bases de datos de usuarios, si tienes cualquier duda sobre la privacidad de tu conexión con nuestro servidor, 
                  puedes escribirnos directamente a <span className="text-sage-400">contacto@carrionbet.com</span>.
                </p>
              </div>
            </div>
          )
        };
      case 'cookies':
        return {
          title: "Política de Cookies",
          content: (
            <div className="space-y-6 text-sm font-light text-slate-400 leading-relaxed">
              <div>
                <h4 className="text-white font-medium mb-2">Uso de Cookies</h4>
                <p>
                  Este sitio web sigue una política de <strong>mínima intervención</strong>. No utilizamos cookies publicitarias, 
                  ni píxeles de rastreo (como Facebook Pixel), ni Google Analytics.
                </p>
                <p className="mt-4">
                  Únicamente se pueden descargar cookies técnicas y de seguridad gestionadas por nuestro proveedor de infraestructura (Cloudflare). 
                  Estas cookies son esenciales para que la web cargue correctamente y sea segura.
                </p>
              </div>

              <div className="p-4 bg-slate-900/30 border border-slate-900">
                <h4 className="text-white font-medium mb-2 text-xs uppercase tracking-widest">Exención de Consentimiento</h4>
                <p>
                  De acuerdo con el artículo 22.2 de la LSSI, las cookies técnicas y de seguridad estrictamente necesarias están exentas 
                  de la obligación de informar y obtener consentimiento del usuario, ya que son indispensables para el funcionamiento del servicio.
                </p>
              </div>

              <div>
                <h4 className="text-white font-medium mb-2">Cookies en Redes Sociales</h4>
                <p>
                  Ten en cuenta que si haces clic en los enlaces a nuestras redes sociales (Telegram, Twitter/X), estarás abandonando este entorno seguro. 
                  Esas plataformas pueden instalar sus propias cookies de seguimiento, las cuales escapan a nuestro control y se rigen por sus propias políticas de privacidad.
                </p>
              </div>
            </div>
          )
        };
    }
  };

  const { title, content } = getContent();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative w-full max-w-2xl bg-slate-950 border border-slate-800 shadow-2xl animate-fade-in-up max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-900 bg-slate-950 sticky top-0 z-10">
          <h3 className="text-xl font-light text-white tracking-widest uppercase">
            {title}<span className="text-sage-400">.</span>
          </h3>
          <button 
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-slate-900"
          >
            <X size={20} strokeWidth={1} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 md:p-10 overflow-y-auto custom-scrollbar">
          {content}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-900 bg-slate-950 sticky bottom-0 z-10 flex justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2 border border-slate-800 text-slate-400 text-xs uppercase tracking-widest hover:bg-sage-400 hover:text-slate-950 hover:border-sage-400 transition-all"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
